import type { App } from '@keboola/api-client/developerPortal/types';

import {
  COMPONENT_TYPES,
  type ComponentType,
  FLAG_BETA,
  FLAG_EXPERIMENTAL,
  KEBOOLA_VENDOR_TYPE,
} from './vocabulary';

/**
 * The API says "app", the product says "component".
 *
 * - Code keeps `App` for the entity and "component" for anything rendered.
 */

/**
 * A component is Keboola's own when its vendor's `type` is `keboola`.
 *
 * - Reads `KEBOOLA_VENDOR_TYPE`, not `KEBOOLA_VENDOR_ID`: any vendor can carry the first-party type.
 */
export const isFirstParty = (app: Pick<App, 'vendor'>): boolean =>
  app.vendor.type === KEBOOLA_VENDOR_TYPE;

const hasFlag = (app: Pick<App, 'uiOptions'>, flag: string): boolean =>
  app.uiOptions?.includes(flag) ?? false;

const isBeta = (app: Pick<App, 'uiOptions'>): boolean => hasFlag(app, FLAG_BETA);
const isExperimental = (app: Pick<App, 'uiOptions'>): boolean => hasFlag(app, FLAG_EXPERIMENTAL);

/**
 * Stack ids of four Snowflake writers indistinguishable by name; not derivable from any field.
 */
const COMPONENT_STACK_LABELS: Record<string, string> = {
  'keboola.wr-db-snowflake': 'AWS',
  'keboola.wr-snowflake-blob-storage': 'Azure',
  'keboola.wr-db-snowflake-gcs': 'GCP BigQuery',
  'keboola.wr-db-snowflake-gcs-s3': 'GCP Snowflake',
};

export const getStackLabel = (appId: string): string | null =>
  COMPONENT_STACK_LABELS[appId] ?? null;

/**
 * A component's stage badge — the single definition of its stage.
 *
 * - Beta wins when both flags are set.
 * - `filterApps` matches the stage filter against this, so filter and badge never disagree.
 */
export const getStageBadge = (app: Pick<App, 'uiOptions'>): 'beta' | 'experimental' | null => {
  if (isBeta(app)) return 'beta';
  if (isExperimental(app)) return 'experimental';
  return null;
};

/** Extractors and writers are shown by their user-facing names; every other type shows its raw value. */
export const getComponentTypeLabel = (type: App['type']): string => {
  if (type === 'extractor') return 'data source';
  if (type === 'writer') return 'data destination';
  return type;
};

const isKnownComponentType = (type: App['type']): type is ComponentType =>
  (COMPONENT_TYPES as readonly string[]).includes(type);

/** Appends the type label only for a known type, so an `other` component shows its bare name. */
export const getAppTitle = (app: Pick<App, 'name' | 'type'>): string =>
  isKnownComponentType(app.type) ? `${app.name} ${getComponentTypeLabel(app.type)}` : app.name;

export const hasIcon = (app: Pick<App, 'icon'>): boolean =>
  Boolean(app.icon && (app.icon['128'] ?? app.icon['64']));

/**
 * The 128px icon only for timestamp-named (`<epoch-ms>.png`) URLs; otherwise the 64px one.
 *
 * - `null` when there is no icon; the caller renders a placeholder.
 */
const TIMESTAMP_FILENAME = /\d{12}\.png$/;

export const getIcon = (app: Pick<App, 'icon'>): string | null => {
  if (!hasIcon(app)) return null;
  const large = app.icon?.['128'];
  if (large === undefined || large === null || !TIMESTAMP_FILENAME.test(large)) {
    return app.icon?.['64'] ?? null;
  }
  return large;
};

export type CatalogFilter = {
  query: string;
  type: string | null;
  stage: string | null;
  /** Categories, OR-ed like kbc-ui's component directory; empty means no category filter. */
  categories: readonly string[];
  /** `true` restricts to first-party components. */
  keboolaOnly: boolean;
};

/**
 * Whether the catalog ever shows this component; every other filter runs on top of it.
 *
 * - ⚠️ `other`-typed and falsy-typed components are excluded, though forms offer `other`.
 */
const isListable = (app: Pick<App, 'type'>): boolean => Boolean(app.type) && app.type !== 'other';

/**
 * Categories carried by at least one listable component, case-insensitively sorted.
 *
 * - Derived from the data, not `COMPONENT_CATEGORIES`, so no option can only ever match nothing.
 */
export const catalogCategories = (apps: readonly Pick<App, 'type' | 'categories'>[]): string[] =>
  [...new Set(apps.filter(isListable).flatMap((app) => app.categories ?? []))].sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase()),
  );

/** Free-text search spans id, name and short description, case-insensitively. */
const matchesQuery = (app: App, query: string): boolean => {
  const needle = query.toLowerCase();
  return (['id', 'name', 'shortDescription'] as const).some((prop) =>
    (app[prop] ?? '').toLowerCase().includes(needle),
  );
};

/**
 * The public catalog's visible set, on top of `isListable`.
 *
 * - Client-side: `GET /apps` cannot filter.
 */
export const filterApps = (apps: App[], filter: CatalogFilter): App[] => {
  let visible = apps.filter(isListable);

  if (filter.type !== null && filter.type !== '') {
    visible = visible.filter((app) => app.type === filter.type);
  }

  // OR within a dimension, AND across dimensions.
  if (filter.categories.length > 0) {
    visible = visible.filter((app) =>
      (app.categories ?? []).some((category) => filter.categories.includes(category)),
    );
  }

  if (filter.keboolaOnly) {
    visible = visible.filter(isFirstParty);
  }

  if (filter.query !== '') {
    visible = visible.filter((app) => matchesQuery(app, filter.query));
  }

  // Matches the badge, not the raw flags, so a Beta + Experimental component files under Beta.
  // An unrecognised stage is a no-op.
  if (filter.stage === 'beta' || filter.stage === 'experimental') {
    visible = visible.filter((app) => getStageBadge(app) === filter.stage);
  }

  return visible;
};
